import { fetchApi } from './client';
import type { AuthUser } from '@/stores/auth-store';
import { normalizeTimeZone } from '@/lib/time/zone';

/**
 * The browser's IANA timezone as its CURRENT id, sent on login/OTP-verify so the
 * backend can render times in the user's own zone in security emails instead of
 * server UTC. Chrome reports India as the legacy 'Asia/Calcutta', which was
 * stored raw and which PostgreSQL 17 rejects; it is sent as 'Asia/Kolkata'.
 *
 * Returns undefined when the zone is unavailable (SSR / older engines) or not a
 * real zone, so the field is simply omitted — unlike `browserTimeZone()`, which
 * falls back to UTC and would overwrite a user's stored zone with it.
 */
function deviceTimeZone(): string | undefined {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? undefined;
  } catch {
    return undefined;
  }
}

export interface LoginResponse {
  /**
   * Absent on the 2FA-challenge shape below. Optional because since RC-E
   * /auth/login can answer `{requiresTwoFactor, tempToken, email}` for an
   * account with TOTP enrolled, and a declared type that contradicts the API is
   * a false premise `tsc` cannot see: the old shape said `tokens` was always
   * present, so `handleLoginSuccess` resolved `accessToken` to '' and wrote
   * that empty string into localStorage instead of routing to /verify-2fa.
   */
  user?: AuthUser;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
  workspaceId?: string;
  // Legacy format compatibility
  accessToken?: string;
  refreshToken?: string;
  /** The account has TOTP enrolled — finish at /verify-2fa with `tempToken`. */
  requiresTwoFactor?: boolean;
  tempToken?: string;
  email?: string;
}

/**
 * A response that IS a session — `user` and `tokens` are always present.
 *
 * Registration cannot answer with a 2FA challenge (a brand-new account has no
 * TOTP enrolment), so its consumers should not have to narrow. Keeping this
 * separate from LoginResponse is what lets LoginResponse tell the truth about
 * the challenge shape without inventing optionality for callers that never see
 * it.
 */
export interface AuthSessionResponse extends LoginResponse {
  user: AuthUser;
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  /** Optional: a person may have one name (INPUT-08). */
  lastName?: string;
  companyName: string;
  workspaceName: string;
  domainName: string;
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    return fetchApi<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, timezone: deviceTimeZone() }),
      skipAuth: true,
    });
  },

  async sendOtp(email: string): Promise<{ message: string }> {
    return fetchApi<{ message: string }>('/auth/otp/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  async verifyOtp(email: string, otp: string): Promise<LoginResponse> {
    return fetchApi<LoginResponse>('/auth/otp/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp, timezone: deviceTimeZone() }),
      skipAuth: true,
    });
  },

  async register(data: RegisterInput): Promise<AuthSessionResponse> {
    return fetchApi<AuthSessionResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  },

  /**
   * Invited-user signup: create the account for an emailed invite token and
   * sign in as the new user. The backend takes email/company/workspace from
   * the INVITE (never the body) and returns the same shape as login, plus the
   * invite's companyId/workspaceId.
   */
  async acceptInviteRegister(data: {
    token: string;
    firstName: string;
    /** Optional: a person may have one name (INPUT-08). */
    lastName?: string;
    password: string;
  }): Promise<LoginResponse & { companyId?: string }> {
    return fetchApi<LoginResponse & { companyId?: string }>(
      '/auth/accept-invite/register',
      {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      },
    );
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    return fetchApi<{ accessToken: string }>('/auth/token/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
    });
  },

  /**
   * Best-effort: record the browser's timezone on the current user so security
   * emails render in their own zone. Called after every login (covers SSO /
   * magic-link redirect flows that can't send it at the API boundary). Requires
   * the just-issued token (NOT skipAuth). Never throws.
   */
  async reportTimezone(): Promise<void> {
    const tz = deviceTimeZone();
    if (!tz) return;
    try {
      await fetchApi<{ ok: boolean }>('/auth/session/timezone', {
        method: 'POST',
        body: JSON.stringify({ timezone: tz }),
      });
    } catch {
      // best-effort — never block the UI on a timezone report
    }
  },

  async logout(refreshToken: string): Promise<void> {
    await fetchApi<void>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
    });
  },

  async getMe(token: string): Promise<AuthUser> {
    return fetchApi<AuthUser>('/auth/me', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      skipAuth: true,
    });
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    return fetchApi<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  async verifyEmail(token: string): Promise<{ message: string }> {
    return fetchApi<{ message: string }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
      skipAuth: true,
    });
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return fetchApi<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      skipAuth: true,
    });
  },

  // ── Passkey Login (public — no JWT required) ──────────────────

  async passkeyLoginOptions(): Promise<{ options: Record<string, unknown> }> {
    return fetchApi<{ options: Record<string, unknown> }>('/auth/passkeys/login-options', {
      method: 'POST',
      body: JSON.stringify({}),
      skipAuth: true,
    });
  },

  async passkeyLoginVerify(response: Record<string, unknown>): Promise<LoginResponse> {
    return fetchApi<LoginResponse>('/auth/passkeys/login-verify', {
      method: 'POST',
      body: JSON.stringify({ response }),
      skipAuth: true,
    });
  },
};
